import {Actions, Button, Dropdown, DropdownDivider, DropdownItem} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';

import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useDirectSaleItemsBulkActions} from './hooks/useDirectSaleItemsBulkActions';
import {useDirectSaleItemsBulkSelection} from './hooks/useDirectSaleItemsBulkSelection';

interface WorkItemsSegmentActionsProps extends RequiredTestIdProps {
  /** Moves Select labour and Select material into More actions, so the row fits its width. */
  isCompact: boolean;
  isEditingDisabled: boolean;
  itemsSelection: ReturnType<typeof useDirectSaleItemsBulkSelection>;
  itemsActions: ReturnType<typeof useDirectSaleItemsBulkActions>;
  onAddLabour: VoidFunction;
  onAddMaterial: VoidFunction;
}

/**
 * The work-item actions of a direct sale, on the right of the request tab segment.
 */
export function WorkItemsSegmentActions(props: WorkItemsSegmentActionsProps) {
  const [directSaleEdit] = usePermissions({
    permissionKeys: ['directSaleEdit'],
  });

  return (
    <HStack align="center" spacing={2}>
      {/* The bulk controls are deliberately NOT gated by the edit permission:
          their applicability is backend row-driven (row.actions), and the identical
          per-row actions stay reachable without directSaleEdit. */}
      <Show when={props.itemsSelection.isAvailable && !props.isEditingDisabled}>
        <Dropdown
          data-testid={suffixTestId('workItemsMoreActions', props)}
          dropdownControl={
            <Button
              variant="ghostLink"
              leftIcon="navigation/more_vert"
              data-testid={suffixTestId('workItemsMoreActionsButton', props)}
              title={i18n.t('entity.orderRequest.actions.moreActions')}
            />
          }
        >
          <Show when={props.isCompact}>
            <DropdownItem
              label={`${i18n.t('entity.orderRequest.actions.selectLabour')} (${props.itemsSelection.labourCount})`}
              onClick={props.itemsSelection.selectLabour}
              isDisabled={props.itemsSelection.isLoading || props.itemsSelection.labourCount === 0}
              data-testid={suffixTestId('selectLabour', props)}
            />
            <DropdownItem
              label={`${i18n.t('entity.orderRequest.actions.selectMaterial')} (${props.itemsSelection.materialCount})`}
              onClick={props.itemsSelection.selectMaterial}
              isDisabled={
                props.itemsSelection.isLoading || props.itemsSelection.materialCount === 0
              }
              data-testid={suffixTestId('selectMaterial', props)}
            />
            <DropdownDivider />
          </Show>
          <DropdownItem
            leftIcon="custom/warehouse"
            label={`${i18n.t('entity.orderRequest.actions.issueMaterial')} (${props.itemsActions.issueCount})`}
            onClick={props.itemsActions.issueMaterial}
            isLoading={props.itemsActions.isIssueMaterialPending}
            isDisabled={
              props.itemsActions.isIssueMaterialPending || props.itemsActions.issueCount === 0
            }
            closeOnClick={false}
            data-testid={suffixTestId('issueMaterial', props)}
          />
          <DropdownItem
            leftIcon="action/shopping_cart"
            label={`${i18n.t('entity.orderRequest.actions.requestMaterial')} (${props.itemsActions.requestCount})`}
            onClick={props.itemsActions.requestMaterial}
            isLoading={props.itemsActions.isRequestMaterialPending}
            isDisabled={
              props.itemsActions.isRequestMaterialPending || props.itemsActions.requestCount === 0
            }
            closeOnClick={false}
            data-testid={suffixTestId('requestMaterial', props)}
          />
          <DropdownItem
            leftIcon="navigation/cancel"
            label={`${i18n.t('entity.orderRequest.actions.requestCancellation')} (${props.itemsActions.cancellationCount})`}
            onClick={props.itemsActions.requestCancellation}
            isLoading={props.itemsActions.isRequestCancellationPending}
            isDisabled={
              props.itemsActions.isRequestCancellationPending ||
              props.itemsActions.cancellationCount === 0
            }
            closeOnClick={false}
            data-testid={suffixTestId('requestCancellation', props)}
          />
          <DropdownItem
            leftIcon="navigation/refresh"
            label={`${i18n.t('entity.warehouse.actions.getCurrentPrice')} (${props.itemsActions.currentPricesCount})`}
            onClick={props.itemsActions.getCurrentPrices}
            isLoading={props.itemsActions.isGetCurrentPricesPending}
            isDisabled={
              props.itemsActions.isGetCurrentPricesPending ||
              props.itemsActions.currentPricesCount === 0
            }
            closeOnClick={false}
            data-testid={suffixTestId('getCurrentPrices', props)}
          />
        </Dropdown>
        <Hide when={props.isCompact}>
          <Button
            variant="ghostLink"
            onClick={props.itemsSelection.selectLabour}
            isDisabled={props.itemsSelection.isLoading || props.itemsSelection.labourCount === 0}
            data-testid={suffixTestId('selectLabour', props)}
            title={`${i18n.t('entity.orderRequest.actions.selectLabour')} (${props.itemsSelection.labourCount})`}
          />
          <Button
            variant="ghostLink"
            onClick={props.itemsSelection.selectMaterial}
            isDisabled={props.itemsSelection.isLoading || props.itemsSelection.materialCount === 0}
            data-testid={suffixTestId('selectMaterial', props)}
            title={`${i18n.t('entity.orderRequest.actions.selectMaterial')} (${props.itemsSelection.materialCount})`}
          />
        </Hide>
      </Show>
      <Actions
        actions={[
          {
            type: 'button',
            title: i18n.t('general.actions.addWork'),
            variant: 'ghostLink',
            leftIcon: 'content/add_circle',
            onClick: props.onAddLabour,
            isDisabled: props.isEditingDisabled || !directSaleEdit,
            tooltip: !directSaleEdit
              ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
              : undefined,
            'data-testid': suffixTestId('requestAction.addLabour', props),
          },
          {
            type: 'button',
            title: i18n.t('general.actions.addMaterial'),
            variant: 'ghostLink',
            leftIcon: 'content/add_circle',
            onClick: props.onAddMaterial,
            isDisabled: props.isEditingDisabled || !directSaleEdit,
            tooltip: !directSaleEdit
              ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
              : undefined,
            'data-testid': suffixTestId('requestAction.addMaterial', props),
          },
        ]}
        data-testid={suffixTestId('listActions', props)}
      />
    </HStack>
  );
}
